import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, RotateCcw, Check, Plus } from 'lucide-react';
import { measureTweet, openTweetIntent, tweetWeight } from '../utils/tweetUtils';

const XIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

/**
 * 남길 조각을 직접 고르는 목록.
 * 길이가 넘칠 때 뒤에서부터 잘라 주면 잘린 부분이 사라져 되돌릴 수 없다.
 */
const SegmentPicker = ({ segments, selectedIds, onToggle }) => (
  <div className="mt-3 rounded-md border border-ink-200 dark:border-ink-800 divide-y divide-ink-100 dark:divide-ink-900 max-h-48 overflow-y-auto">
    {segments.map(segment => {
      const on = selectedIds.includes(segment.id);
      return (
        <button
          key={segment.id}
          type="button"
          onClick={() => onToggle(segment.id)}
          aria-pressed={on}
          className={[
            'flex w-full items-start gap-2 px-3 py-2 text-left transition-colors',
            on
              ? 'text-ink-900 dark:text-ink-100'
              : 'text-ink-400 dark:text-ink-500 hover:text-ink-600 dark:hover:text-ink-300'
          ].join(' ')}
        >
          <span className="mt-0.5 inline-flex w-3.5 shrink-0 justify-center">
            {on
              ? <Check size={13} strokeWidth={2} />
              : <Plus size={13} strokeWidth={1.5} />}
          </span>
          <span className={`flex-1 text-xs leading-relaxed font-sans ${on ? '' : 'line-through decoration-ink-300 dark:decoration-ink-700'}`}>
            {segment.text}
          </span>
          <span className="mt-0.5 shrink-0 text-[11px] tabular-nums text-ink-500 dark:text-ink-400">
            {tweetWeight(segment.text)}
          </span>
        </button>
      );
    })}
  </div>
);

/**
 * X 공유 — 문구를 고르고 다듬은 뒤 내보낸다.
 *
 * @param {Array} templates 미리 만들어진 문구 후보.
 *   `segments` 와 `assemble(selectedIds)` 를 가진 문구는 조각을 골라 담을 수 있다.
 */
const ShareModal = ({ open, title = '공유', templates = [], onClose }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [text, setText] = useState('');
  const [picked, setPicked] = useState([]);

  const selected = templates.find(t => t.id === selectedId) || templates[0];
  const segments = selected?.segments || null;

  useEffect(() => {
    if (!open || templates.length === 0) return;
    const first = templates[0];
    setSelectedId(first.id);
    setText(first.text);
    setPicked(first.defaultSelected || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const pick = (template) => {
    setSelectedId(template.id);
    setText(template.text);
    setPicked(template.defaultSelected || []);
  };

  /** 조각을 켜고 끄면 본문을 다시 만든다 (직접 고친 내용은 덮어쓴다) */
  const toggleSegment = (segmentId) => {
    const next = picked.includes(segmentId)
      ? picked.filter(id => id !== segmentId)
      : [...picked, segmentId];
    // 원래 조각 순서를 지킨다 — 고른 차례대로 붙으면 문장이 뒤섞인다
    const ordered = segments.filter(s => next.includes(s.id)).map(s => s.id);
    setPicked(ordered);
    setText(selected.assemble(ordered));
  };

  const m = measureTweet(text);
  const empty = !text.trim();

  const handleSend = () => {
    if (m.blocked || empty) return;
    openTweetIntent(text);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-950/50" onClick={onClose} />

      <div className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-lg border border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-950 shadow-2xl">
        <div className="sticky top-0 flex items-center justify-between px-5 h-14 border-b border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-950">
          <h2 className="font-display text-lg">{title}</h2>
          <button
            onClick={onClose}
            className="p-1.5 text-ink-500 dark:text-ink-400 hover:text-ink-900 dark:hover:text-ink-100 transition-colors"
            aria-label="닫기"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-5">
          {templates.length === 0 ? (
            <p className="py-10 text-center text-sm text-ink-600 dark:text-ink-300 font-sans">
              아직 공유할 만한 기록이 없어요
            </p>
          ) : (
            <>
              {templates.length > 1 && (
                <div className="flex flex-wrap gap-2">
                  {templates.map(t => (
                    <button
                      key={t.id}
                      onClick={() => pick(t)}
                      title={t.hint}
                      className={`chip !py-1.5 !text-xs ${selected?.id === t.id ? 'chip-active' : ''}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}

              {segments && segments.length > 1 && (
                <>
                  <div className="mt-4 flex items-baseline justify-between gap-2">
                    <span className="label">남길 문장 고르기</span>
                    <span className="text-[11px] text-ink-500 dark:text-ink-400 font-sans tabular-nums">
                      {picked.length} / {segments.length}
                    </span>
                  </div>
                  <SegmentPicker
                    segments={segments}
                    selectedIds={picked}
                    onToggle={toggleSegment}
                  />
                  {selected.assemble && text !== selected.assemble(picked) && (
                    <p className="mt-1.5 text-[11px] text-ink-500 dark:text-ink-400 font-sans">
                      아래에서 직접 고친 문구예요. 문장을 다시 고르면 덮어씁니다
                    </p>
                  )}
                </>
              )}

              <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                rows={6}
                lang="ko"
                className="field mt-4 resize-y leading-relaxed"
              />

              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                <button
                  onClick={() => selected && pick(selected)}
                  className="text-xs text-ink-500 dark:text-ink-400 hover:text-ink-900 dark:hover:text-ink-100 transition-colors font-sans inline-flex items-center gap-1"
                >
                  <RotateCcw size={12} strokeWidth={1.5} />
                  처음 문구로
                </button>

                <div className="flex-1" />

                {/* 두 기준을 같이 보여준다 — 한글은 X 쪽이 먼저 걸린다 */}
                <span
                  className={[
                    'text-xs tabular-nums font-sans',
                    m.overChars ? 'text-ink-900 dark:text-ink-50 font-semibold' : 'text-ink-500 dark:text-ink-400'
                  ].join(' ')}
                >
                  글자 {m.chars}/{m.charLimit}
                </span>
                <span
                  className={[
                    'text-xs tabular-nums font-sans',
                    m.overWeight ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-ink-500 dark:text-ink-400'
                  ].join(' ')}
                  title="X는 한글 1자를 2자로 셉니다 (상한 280)"
                >
                  X {m.weight}/{m.weightLimit}
                </span>
              </div>

              {m.overWeight && (
                <p className="mt-2 text-xs leading-relaxed text-red-600 dark:text-red-400 font-sans">
                  X 상한을 {m.weight - m.weightLimit} 넘었어요. 한글은 1자가 2로 계산돼서
                  한글만 쓰면 140자가 실질 한계입니다.
                </p>
              )}
            </>
          )}

          <div className="mt-5 flex items-center justify-end gap-2">
            <button onClick={onClose} className="btn-ghost text-xs">취소</button>
            <button
              onClick={handleSend}
              disabled={m.blocked || empty}
              className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <XIcon />
              X에 올리기
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ShareModal;
